import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Award,
  Building2,
  CheckCircle2,
  Download,
  ExternalLink,
  FileText,
  GalleryHorizontalEnd,
  Landmark,
  Mail,
  MapPin,
  Medal,
  Navigation,
  Phone,
  Route,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  Trophy,
  Users,
} from "lucide-react";
import "../../styles/institutional-pages.css";

const fallbackText = "Information will be updated by the directorate office.";

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

function safeImage(src, fallback = "/fpb.png") {
  if (!src) return fallback;
  if (src.startsWith("http") || src.startsWith("/")) return src;
  return `/${src.replace(/^\/+/, "")}`;
}

function toParagraphs(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || "")
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function textFromItems(items = [], fallback = fallbackText) {
  const parts = (items || [])
    .map((item) => item.description || item.title || item.value)
    .filter(Boolean);
  return parts.length ? parts.join("\n\n") : fallback;
}

function AnimatedSection({ id, children, className = "bg-white", compact = false }) {
  return (
    <motion.section
      id={id}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.12 }}
      className={`directorate-classic-section ${className} rounded-2xl shadow-lg ${compact ? "p-5 md:p-6" : "p-6 md:p-8"}`}
    >
      {children}
    </motion.section>
  );
}

function SectionTitle({ icon: Icon, eyebrow, children, light = false }) {
  return (
    <div className="directorate-section-title-wrap">
      {eyebrow && <p className={light ? "text-xs font-black uppercase tracking-[0.2em] text-cyan-100" : "text-xs font-black uppercase tracking-[0.2em] text-blue-700"}>{eyebrow}</p>}
      <h2 className={`mt-2 text-xl md:text-2xl font-black flex items-center gap-3 ${light ? "text-white" : "text-slate-950"}`}>
        {Icon && <Icon className={light ? "text-cyan-200" : "text-blue-700"} />}
        {children}
      </h2>
    </div>
  );
}

const Divider = ({ light = false }) => (
  <motion.div
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    transition={{ duration: 0.75 }}
    viewport={{ once: true }}
    className={`origin-left h-1 rounded-full my-6 ${light ? "bg-gradient-to-r from-cyan-200 to-white/50" : "bg-gradient-to-r from-blue-800 to-cyan-400"}`}
  />
);

const badgeThemes = [
  "bg-blue-100 text-blue-800 border-blue-200",
  "bg-cyan-100 text-cyan-800 border-cyan-200",
  "bg-indigo-100 text-indigo-800 border-indigo-200",
  "bg-emerald-100 text-emerald-800 border-emerald-200",
  "bg-amber-100 text-amber-800 border-amber-200",
  "bg-purple-100 text-purple-800 border-purple-200",
  "bg-rose-100 text-rose-800 border-rose-200",
];

const iconMap = [Medal, Award, Star, Trophy, ShieldCheck, Landmark, Sparkles];

function getProgressIcon(progress, index) {
  const rawIcon = String(progress.icon || progress.meta || "").trim();
  if (rawIcon && rawIcon.length <= 4) return rawIcon;
  const Icon = iconMap[index % iconMap.length];
  return <Icon size={18} />;
}

function ProgressBadges({ items = [], reduceMotion }) {
  const list = (items || []).filter((item) => item && (item.title || item.value || item.description));
  if (!list.length) return null;

  return (
    <div className="directorate-progress-badges flex flex-wrap gap-3" aria-label="Directorate progress and achievements">
      {list.map((item, index) => {
        const label = item.value ? `${item.value} ${item.title || ""}`.trim() : (item.title || item.description);
        return (
          <motion.span
            key={`${label}-${index}`}
            whileHover={reduceMotion ? undefined : { y: -4, scale: 1.035 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-bold shadow-sm ${badgeThemes[index % badgeThemes.length]}`}
            title={item.description || label}
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/70 shadow-sm">{getProgressIcon(item, index)}</span>
            <span>{label}</span>
          </motion.span>
        );
      })}
    </div>
  );
}

function extractIframeSrc(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  const match = text.match(/src=["']([^"']+)["']/i);
  return match ? match[1] : text;
}

function mapEmbedUrl(mapUrl, address) {
  const raw = extractIframeSrc(mapUrl);
  if (raw && raw.includes("google.com/maps")) {
    if (raw.includes("/embed") || raw.includes("output=embed")) return raw;
    return `${raw}${raw.includes("?") ? "&" : "?"}output=embed`;
  }
  if (raw && raw.startsWith("http")) return raw;
  const query = address || "Federal Polytechnic Bida, Niger State, Nigeria";
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
}

function DirectorateMapSection({ contact = {}, mapPoints = [], reduceMotion }) {
  const mainPoint = mapPoints.find((item) => item.mapUrl || item.address) || null;
  const address = mainPoint?.address || contact.address || "Federal Polytechnic Bida, Niger State, Nigeria";
  const embedSrc = mapEmbedUrl(mainPoint?.mapUrl || contact.mapUrl, address);
  const title = mainPoint?.title || "Directorate Location";

  return (
    <AnimatedSection id="map" className="bg-white overflow-hidden">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <SectionTitle icon={MapPin} eyebrow="Locate Us">Directorate Map & Office Location</SectionTitle>
          <p className="mt-2 max-w-3xl text-gray-600">Show visitors where to find the directorate office, service desk, technical office and other important support points.</p>
        </div>
        {(mainPoint?.mapUrl || contact.mapUrl) && (
          <a href={extractIframeSrc(mainPoint?.mapUrl || contact.mapUrl)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-blue-800 px-4 py-3 text-sm font-bold text-white shadow hover:bg-blue-700">
            <ExternalLink size={18} /> Open Map
          </a>
        )}
      </div>
      <Divider />
      <div className="directorate-map-grid grid gap-6 lg:grid-cols-[1fr_360px]">
        <motion.div whileHover={reduceMotion ? undefined : { scale: 1.01 }} className="directorate-map-frame min-h-[320px] overflow-hidden rounded-2xl border border-blue-100 bg-blue-50 shadow-inner">
          <iframe title={title} src={embedSrc} width="100%" height="100%" loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-[320px] w-full border-0 md:h-[420px]"></iframe>
        </motion.div>
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-blue-800 p-3 text-white"><Navigation size={22} /></div>
              <div>
                <h3 className="font-black text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{address}</p>
              </div>
            </div>
          </div>
          {(mapPoints.length ? mapPoints : [{ title: "Directorate Office", address }]).map((point, index) => (
            <motion.div key={index} whileHover={reduceMotion ? undefined : { x: 4 }} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-blue-50 p-2 text-blue-700"><Route size={18} /></div>
                <div>
                  <h4 className="font-bold text-slate-900">{point.title || `Location ${index + 1}`}</h4>
                  <p className="mt-1 text-sm leading-6 text-slate-600">{point.address || point.note || address}</p>
                  {point.note && <p className="mt-1 text-xs font-bold uppercase tracking-wide text-blue-700">{point.note}</p>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
}

function InfoCard({ icon: Icon, title, value }) {
  return (
    <motion.div whileHover={{ y: -5 }} className="rounded-2xl border border-white/20 bg-white/15 p-4 text-white shadow-lg backdrop-blur-md">
      <div className="flex items-center gap-3">
        <span className="rounded-xl bg-white/15 p-2"><Icon size={20} /></span>
        <div>
          <p className="text-xs uppercase tracking-widest text-white/70">{title}</p>
          <p className="font-black">{value}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function DirectorateClassicPage({ data = {} }) {
  const reduceMotion = useReducedMotion();
  const title = data.title || data.shortName || "Directorate";
  const heroImage = safeImage(data.heroImage, "/images/itsb.jpg");
  const overview = toParagraphs(data.overview);
  const leader = data.leader || null;
  const contact = data.contact || {};
  const offerings = data.offerings || [];
  const features = data.features || [];
  const downloads = data.downloads || [];
  const gallery = data.gallery || [];
  const faqs = data.faqs || [];
  const timeline = data.timeline || [];
  const quickInfo = data.quickInfo || [];
  const quickStats = data.quickStats || [];
  const mapPoints = data.mapPoints || [];
  const progress = leader?.progress?.length
    ? leader.progress
    : (leader?.achievements || []).map((item) => (typeof item === "string" ? { title: item } : item));

  const navItems = [
    ["overview", "Overview"],
    ["director", "Director"],
    ["mandate", "Mandate"],
    ["services", "Services"],
    ["milestones", "Milestones"],
    ["downloads", "Downloads"],
    ["gallery", "Gallery"],
    ["map", "Map"],
    ["contact", "Contact"],
  ].filter(([id]) => {
    if (id === "director") return !!leader;
    if (id === "milestones") return timeline.length > 0;
    if (id === "downloads") return downloads.length > 0;
    if (id === "gallery") return gallery.length > 0;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 directorate-classic-page">
      <nav className="sticky top-0 z-40 bg-white/95 backdrop-blur-md shadow-md py-3 px-4 md:px-20 flex gap-6 items-center overflow-x-auto whitespace-nowrap">
        <div className="flex-1 min-w-[260px] font-black text-blue-800">
          {data.shortName || title} — Federal Polytechnic Bida
        </div>
        <div className="flex gap-4 text-sm font-semibold">
          {navItems.map(([href, label]) => <a key={href} href={`#${href}`} className="hover:text-blue-600">{label}</a>)}
        </div>
      </nav>

      <header className="relative min-h-[440px] overflow-hidden bg-slate-950">
        <motion.img
          src={heroImage}
          alt={`${title} banner`}
          className="absolute inset-0 h-full w-full object-cover opacity-70"
          initial={reduceMotion ? false : { scale: 1.1 }}
          animate={reduceMotion ? undefined : { scale: 1.035 }}
          transition={{ duration: 3.2, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-blue-950/80 to-slate-950/50" />
        <motion.div aria-hidden="true" animate={reduceMotion ? undefined : { y: [0, -16, 0], x: [0, 12, 0] }} transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }} className="absolute right-16 top-20 h-36 w-36 rounded-full bg-cyan-400/20 blur-3xl" />
        <motion.div aria-hidden="true" animate={reduceMotion ? undefined : { y: [0, 14, 0], x: [0, -10, 0] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }} className="absolute left-10 bottom-14 h-44 w-44 rounded-full bg-blue-500/20 blur-3xl" />

        <div className="relative z-10 mx-auto flex min-h-[440px] max-w-6xl flex-col justify-center px-4 py-16 md:px-8">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" className="max-w-4xl text-white">
            <span className="inline-flex rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] backdrop-blur-md">Directorate</span>
            <h1 className="mt-5 text-4xl font-black leading-tight md:text-6xl">{title}</h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-white/85">{data.subtitle || fallbackText}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={data.primaryAction?.href || "#contact"} className="rounded-xl bg-cyan-400 px-5 py-3 font-black text-blue-950 shadow hover:bg-cyan-300">{data.primaryAction?.label || "Contact Directorate"}</a>
              <a href={data.secondaryAction?.href || "#services"} className="rounded-xl border border-white/25 bg-white/10 px-5 py-3 font-black text-white shadow backdrop-blur-md hover:bg-white/20">{data.secondaryAction?.label || "Explore Services"}</a>
            </div>
          </motion.div>

          <div className="mt-10 grid gap-4 md:grid-cols-4">
            {(quickStats.length ? quickStats : [
              { value: "Policy", label: "Coordination" },
              { value: "Support", label: "Service delivery" },
              { value: "Staff", label: "Institutional users" },
              { value: "Reports", label: "Documentation" },
            ]).slice(0, 4).map((stat, index) => (
              <InfoCard key={index} icon={index === 0 ? Target : index === 1 ? Users : index === 2 ? Building2 : FileText} title={stat.label || stat.title || "Info"} value={stat.value || stat.title || "—"} />
            ))}
          </div>
        </div>
      </header>

      <main className="px-4 py-12 md:px-20 space-y-10 md:space-y-12">
        <AnimatedSection id="overview">
          <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
            <div>
              <SectionTitle icon={Building2} eyebrow="Overview">Directorate In Brief</SectionTitle>
              <Divider />
              {(overview.length ? overview : [fallbackText]).map((paragraph, index) => (
                <p key={index} className="mt-4 text-justify leading-8 text-gray-700 first:mt-0">{paragraph}</p>
              ))}
            </div>
            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 shadow-inner">
              <h3 className="font-black text-blue-900">Quick Information</h3>
              <div className="mt-4 space-y-3">
                {(quickInfo.length ? quickInfo : [
                  { label: "Type", value: "Directorate" },
                  { label: "Group", value: data.parentGroup || "Federal Polytechnic Bida" },
                  { label: "Location", value: contact.address || "Federal Polytechnic Bida" },
                ]).map((item, index) => (
                  <div key={index} className="flex justify-between gap-4 border-b border-blue-100 pb-2 text-sm last:border-0">
                    <span className="font-bold text-slate-500">{item.label}</span>
                    <span className="text-right font-black text-slate-800">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {(data.vision || data.mission) && (
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {data.vision && <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-slate-200"><h3 className="font-black text-blue-800">Vision</h3><p className="mt-2 leading-7 text-gray-700">{data.vision}</p></div>}
              {data.mission && <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-slate-200"><h3 className="font-black text-blue-800">Mission</h3><p className="mt-2 leading-7 text-gray-700">{data.mission}</p></div>}
            </div>
          )}
        </AnimatedSection>

        {leader && (
          <AnimatedSection id="director" className="relative overflow-hidden bg-white">
            <div className="absolute right-0 top-0 h-48 w-48 rounded-bl-full bg-blue-50" />
            <div className="relative grid gap-8 md:grid-cols-[260px_1fr] md:items-start">
              <motion.img
                src={safeImage(leader.photo, "/fpb.png")}
                alt={leader.name || "Director"}
                whileHover={reduceMotion ? undefined : { rotate: 2, scale: 1.035 }}
                transition={{ type: "spring", stiffness: 200, damping: 14 }}
                className="h-56 w-56 rounded-3xl object-cover shadow-2xl ring-4 ring-white md:h-64 md:w-64"
              />
              <div>
                <SectionTitle icon={Users} eyebrow="Leadership">{leader.sectionTitle || "Message from the Director"}</SectionTitle>
                <Divider />
                <h3 className="text-2xl font-black text-slate-950">{leader.name}</h3>
                <p className="mt-1 font-bold text-blue-700">{leader.title || "Director"}</p>
                <ProgressBadges items={progress} reduceMotion={reduceMotion} />
                {leader.quote && <div className="mt-6 rounded-2xl border-l-4 border-blue-700 bg-blue-50 p-5 shadow-sm"><p className="italic font-semibold leading-7 text-blue-950">“{leader.quote}”</p></div>}
                {(toParagraphs(leader.bio).length ? toParagraphs(leader.bio) : [fallbackText]).map((paragraph, index) => (
                  <p key={index} className="mt-4 text-justify leading-8 text-gray-700">{paragraph}</p>
                ))}
              </div>
            </div>
          </AnimatedSection>
        )}

        <AnimatedSection id="mandate" className="bg-blue-950 text-white">
          <SectionTitle icon={Target} eyebrow="Mandate" light>Core Mandate and Responsibilities</SectionTitle>
          <Divider light />
          <p className="max-w-4xl text-justify leading-8 text-white/85">{textFromItems(offerings, "The directorate coordinates specialized institutional services, policy implementation, technical support, documentation and administrative guidance within its approved mandate.")}</p>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {(offerings.length ? offerings : features).slice(0, 6).map((item, index) => (
              <motion.div key={index} whileHover={reduceMotion ? undefined : { y: -6 }} className="rounded-2xl border border-white/10 bg-white/10 p-5 shadow-lg backdrop-blur-md">
                <CheckCircle2 className="text-cyan-200" />
                <h3 className="mt-4 font-black">{item.title || `Mandate ${index + 1}`}</h3>
                <p className="mt-2 text-sm leading-6 text-white/75">{item.description || item.meta}</p>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection id="services">
          <SectionTitle icon={Sparkles} eyebrow="Service Delivery">Directorate Service Areas</SectionTitle>
          <Divider />
          <div className="grid gap-5 md:grid-cols-3">
            {(features.length ? features : offerings).map((item, index) => (
              <motion.div key={index} whileHover={reduceMotion ? undefined : { y: -7, scale: 1.01 }} className="rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-700 text-white"><Award size={20} /></div>
                <h3 className="mt-4 font-black text-slate-950">{item.title || `Service ${index + 1}`}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{item.description || item.meta}</p>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>

        {timeline.length > 0 && (
          <AnimatedSection id="milestones">
            <SectionTitle icon={Landmark} eyebrow="Progress">Directorate Milestones</SectionTitle>
            <Divider />
            <div className="space-y-3">
              {timeline.map((item, index) => (
                <motion.div key={index} whileHover={reduceMotion ? undefined : { x: 4 }} className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[120px_1fr]">
                  <span className="font-black text-blue-700">{item.year || item.value}</span>
                  <div><h3 className="font-black text-slate-900">{item.title}</h3><p className="mt-1 text-sm leading-6 text-slate-600">{item.description}</p></div>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>
        )}

        {downloads.length > 0 && (
          <AnimatedSection id="downloads">
            <SectionTitle icon={Download} eyebrow="Resources">Directorate Downloads and Useful Links</SectionTitle>
            <Divider />
            <div className="grid gap-3 md:grid-cols-2">
              {downloads.map((item, index) => (
                <a key={index} href={item.href || "#"} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 p-4 font-bold text-slate-800 hover:border-blue-300 hover:bg-blue-50">
                  <span className="flex items-center gap-3"><FileText size={18} /> {item.label || item.title}</span>
                  <Download size={18} />
                </a>
              ))}
            </div>
          </AnimatedSection>
        )}

        {gallery.length > 0 && (
          <AnimatedSection id="gallery">
            <SectionTitle icon={GalleryHorizontalEnd} eyebrow="Gallery">Directorate Gallery</SectionTitle>
            <Divider />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {gallery.map((item, index) => (
                <motion.figure key={index} whileHover={reduceMotion ? undefined : { y: -6 }} className="overflow-hidden rounded-2xl bg-slate-950 shadow-lg">
                  <img src={safeImage(item.src, "/fpb.png")} alt={item.caption || "Directorate gallery"} className="h-44 w-full object-cover opacity-90" />
                  <figcaption className="p-3 text-sm font-bold text-white">{item.caption || "Directorate activity"}</figcaption>
                </motion.figure>
              ))}
            </div>
          </AnimatedSection>
        )}

        <DirectorateMapSection contact={contact} mapPoints={mapPoints} reduceMotion={reduceMotion} />

        {faqs.length > 0 && (
          <AnimatedSection id="faq">
            <SectionTitle icon={ShieldCheck} eyebrow="FAQ">Frequently Asked Questions</SectionTitle>
            <Divider />
            <div className="space-y-3">
              {faqs.map((item, index) => (
                <details key={index} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <summary className="cursor-pointer font-black text-slate-900">{item.question}</summary>
                  <p className="mt-3 leading-7 text-slate-600">{item.answer}</p>
                </details>
              ))}
            </div>
          </AnimatedSection>
        )}

        <AnimatedSection id="contact">
          <SectionTitle icon={Mail} eyebrow="Contact">Contact the Directorate</SectionTitle>
          <Divider />
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-slate-200"><MapPin className="text-blue-700" /><h3 className="mt-3 font-black">Address</h3><p className="mt-2 text-sm leading-6 text-slate-600">{contact.address || "Federal Polytechnic Bida"}</p></div>
            <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-slate-200"><Phone className="text-blue-700" /><h3 className="mt-3 font-black">Phone</h3><p className="mt-2 text-sm leading-6 text-slate-600">{contact.phone || "To be updated"}</p></div>
            <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-slate-200"><Mail className="text-blue-700" /><h3 className="mt-3 font-black">Email</h3><p className="mt-2 text-sm leading-6 text-slate-600">{contact.email || "To be updated"}</p></div>
          </div>
        </AnimatedSection>
      </main>
    </div>
  );
}
